// src/components/FriendsPanel.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import pb from '../lib/pb';
import { useAuth } from '../contexts/AuthContext';
import { injectStyles } from '../shared/PostCard';
import FriendTile, { FriendAvatar, userName } from './FriendTile';
import PeopleAltRoundedIcon from '@mui/icons-material/PeopleAltRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import ChatBubbleRoundedIcon from '@mui/icons-material/ChatBubbleRounded';
import CallRoundedIcon from '@mui/icons-material/CallRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import BlockRoundedIcon from '@mui/icons-material/BlockRounded';
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded';
import PersonRemoveRoundedIcon from '@mui/icons-material/PersonRemoveRounded';

/**
 * FriendsPanel
 * - mode 'sidebar' (default): embeddable card. 'compact' is accepted as an alias.
 * - mode 'full': the same panel as a centered modal.
 *
 * Collapsing (only when `collapsible`):
 * - collapseDirection 'side'     -> header shows a LEFT arrow; collapsed it becomes a slim rail
 *                                   with a RIGHT arrow to broaden it again (desktop column).
 * - collapseDirection 'vertical' -> header chevron folds the card body (tablet card). Default.
 * - Pass `collapsed` + `onToggleCollapsed` to control it from outside (Feed does this so the
 *   grid column can resize); otherwise the panel keeps its own state.
 *
 * Other props: onClose, className, onReceivedCount(n)
 */

const COOLDOWN_MS = 24 * 60 * 60 * 1000;
const PAGE_SIZE = 8;
const POLL_MS = 30000;

const REL_LABEL = { friend: 'Friend', family: 'Family', stranger: 'Stranger' };
const relLabel = (t) => REL_LABEL[t] || (t ? t[0].toUpperCase() + t.slice(1) : 'Relation');

const waitLabel = (iso) => {
  const ms = new Date(iso).getTime() - Date.now();
  if (!(ms > 0)) return '';
  const h = Math.floor(ms / 3.6e6);
  const m = Math.max(1, Math.ceil((ms % 3.6e6) / 6e4));
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

const inCooldown = (r) => !!r.resendAvailableAt && new Date(r.resendAvailableAt).getTime() > Date.now();

/* ------------------------------------------------------------------ */
/* Styles                                                              */
/* ------------------------------------------------------------------ */
const FRIENDS_CSS = `
.fr-card{position:relative;display:flex;flex-direction:column;min-width:0;min-height:0;background:var(--fr-bg);border:1px solid var(--fr-border);
  border-radius:18px;box-shadow:var(--fr-shadow);overflow:hidden;color:var(--fr-text)}
.fr-head{display:flex;align-items:center;gap:10px;padding:14px 14px 10px;flex:none}
.fr-title{margin:0;flex:1;min-width:0;display:flex;align-items:center;gap:9px;font-size:16px;font-weight:800}
.fr-title-ico{width:30px;height:30px;border-radius:10px;background:var(--fr-grad);color:#fff;display:grid;place-items:center;flex:none}
.fr-title-ico svg{font-size:18px}
.fr-new{font-size:11.5px;font-weight:800;padding:2px 8px;border-radius:999px;background:var(--fr-accent);color:#fff}
.fr-iconbtn{width:34px;height:34px;border-radius:10px;border:0;background:var(--fr-bg2);color:var(--fr-muted);display:inline-grid;place-items:center;
  cursor:pointer;flex:none;transition:background-color .15s,color .15s}
.fr-iconbtn:hover{background:var(--fr-bg3);color:var(--fr-text)}
.fr-iconbtn:focus-visible{outline:2px solid var(--fr-accent);outline-offset:2px}
.fr-iconbtn svg{font-size:20px}
.fr-chev svg{transition:transform .2s}
.fr-card:not(.is-collapsed) .fr-chev svg{transform:rotate(180deg)}
.fr-card.is-collapsed .fr-fold{display:none}
.fr-card.is-collapsed .fr-head{padding-bottom:14px}

/* collapsed rail (desktop column) */
.fr-rail{display:flex;flex-direction:column;align-items:center;gap:14px;width:100%;padding:12px 0 18px;background:var(--fr-bg);border:1px solid var(--fr-border);
  border-radius:18px;box-shadow:var(--fr-shadow);color:var(--fr-text)}
.fr-rail-ico{position:relative;width:36px;height:36px;border-radius:12px;background:var(--fr-grad);color:#fff;display:grid;place-items:center}
.fr-rail-badge{position:absolute;top:-6px;right:-8px;min-width:18px;height:18px;padding:0 5px;border-radius:999px;background:#ef4444;color:#fff;
  font-size:11px;font-weight:800;display:grid;place-items:center;box-shadow:0 0 0 2px var(--fr-bg)}
.fr-rail-label{writing-mode:vertical-rl;font-size:12px;font-weight:800;letter-spacing:.1em;text-transform:uppercase;color:var(--fr-muted)}

.fr-tabs{flex:none;display:flex;gap:2px;margin:0 14px;padding:3px;background:var(--fr-bg2);border-radius:999px;overflow-x:auto;scrollbar-width:none}
.fr-tabs::-webkit-scrollbar{display:none}
.fr-tab{flex:none;display:inline-flex;align-items:center;gap:6px;border:0;background:transparent;color:var(--fr-muted);font:inherit;font-weight:700;font-size:12.5px;
  padding:7px 11px;border-radius:999px;cursor:pointer;white-space:nowrap;transition:background-color .15s,color .15s}
.fr-tab:hover{color:var(--fr-text)}
.fr-tab:focus-visible{outline:2px solid var(--fr-accent);outline-offset:1px}
.fr-tab.is-active{background:var(--fr-tab);color:var(--fr-text);box-shadow:0 1px 3px rgba(0,0,0,.22)}
.fr-tab-n{min-width:18px;height:18px;padding:0 5px;border-radius:999px;font-size:11px;font-weight:800;display:inline-grid;place-items:center;background:var(--fr-bg3);color:var(--fr-muted)}
.fr-tab-n.is-alert{background:var(--fr-accent);color:#fff}

.fr-search{flex:none;position:relative;margin:10px 14px 0}
.fr-search svg{position:absolute;left:10px;top:50%;transform:translateY(-50%);font-size:18px;color:var(--fr-faint);pointer-events:none}
.fr-search input{width:100%;height:38px;padding:0 12px 0 34px;border-radius:12px;border:1px solid var(--fr-border-strong);background:var(--fr-bg2);
  color:var(--fr-text);font:inherit;font-size:14px;outline:none}
.fr-search input::placeholder{color:var(--fr-faint)}
.fr-search input:focus{border-color:var(--fr-accent);box-shadow:0 0 0 3px var(--fr-accent-soft)}

.fr-body{flex:1;min-height:0;display:flex;flex-direction:column;gap:10px;padding:12px 14px 14px;overflow-y:auto;overscroll-behavior:contain}
.fr-more{align-self:center}

/* ---------- rows (relations / received / requested / blocked) ---------- */
.fr-row{display:flex;flex-wrap:wrap;align-items:center;gap:10px;padding:10px;border-radius:14px;background:var(--fr-bg2);border:1px solid var(--fr-border);min-width:0}
.fr-row-main{flex:1 1 140px;display:flex;align-items:center;gap:10px;min-width:0;padding:0;border:0;background:none;color:inherit;font:inherit;text-align:left;cursor:pointer}
.fr-row-main:focus-visible{outline:2px solid var(--fr-accent);outline-offset:2px;border-radius:8px}
.fr-row-text{display:flex;flex-direction:column;gap:2px;min-width:0}
.fr-row-name{font-size:14px;font-weight:700;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.fr-row-sub{font-size:12px;color:var(--fr-faint);display:flex;align-items:center;gap:6px;flex-wrap:wrap}
.fr-row-actions{display:flex;align-items:center;gap:6px;flex-wrap:wrap;margin-left:auto}
.fr-pill{font-size:11px;font-weight:800;padding:2px 8px;border-radius:999px;background:var(--fr-accent-soft);color:var(--fr-accent)}
.fr-pill--warn{background:color-mix(in srgb,var(--fr-warn) 16%,transparent);color:var(--fr-warn)}
.fr-pill--muted{background:var(--fr-bg3);color:var(--fr-muted)}

/* ---------- empty / skeleton / login ---------- */
.fr-empty{text-align:center;padding:28px 12px;color:var(--fr-muted);font-size:13.5px;line-height:1.5}
.fr-empty-ico{width:48px;height:48px;border-radius:14px;margin:0 auto 10px;display:grid;place-items:center;background:var(--fr-accent-soft);color:var(--fr-accent);font-size:22px}
.fr-empty h4{margin:0 0 4px;font-size:15px;color:var(--fr-text)}
.fr-empty p{margin:0}
.fr-empty .fr-btn{margin-top:12px}
.fr-sk{border-radius:14px;height:84px;background:linear-gradient(90deg,var(--fr-bg2) 25%,var(--fr-bg3) 37%,var(--fr-bg2) 63%);background-size:400% 100%;animation:fr-shimmer 1.4s ease infinite}
@keyframes fr-shimmer{0%{background-position:100% 50%}100%{background-position:0 50%}}

/* ---------- confirm ---------- */
.fr-confirm{position:absolute;inset:0;z-index:5;display:grid;place-items:center;padding:16px;background:var(--fr-overlay);animation:fr-fade .15s ease-out}
.fr-confirm-box{width:100%;max-width:320px;padding:18px;border-radius:16px;background:var(--fr-bg);border:1px solid var(--fr-border-strong);box-shadow:0 20px 50px rgba(0,0,0,.4)}
.fr-confirm-box h4{margin:0 0 6px;font-size:16px}
.fr-confirm-box p{margin:0 0 16px;font-size:13.5px;line-height:1.5;color:var(--fr-muted)}
.fr-confirm-btns{display:flex;gap:8px;justify-content:flex-end}
@keyframes fr-fade{from{opacity:0}to{opacity:1}}

/* ---------- modal mode ---------- */
.fr-modal{position:fixed;inset:0;z-index:1200;display:flex;align-items:flex-start;justify-content:center;padding:clamp(10px,5vh,56px) 10px;background:var(--fr-overlay)}
.fr-modal .fr-card{width:100%;max-width:640px;max-height:100%;animation:fr-pop .2s ease-out}
@keyframes fr-pop{from{transform:translateY(10px) scale(.98);opacity:0}to{transform:none;opacity:1}}

/* ---------- toasts (portaled to <body>, always dark) ---------- */
.fr-toasts{position:fixed;top:14px;right:14px;z-index:4000;display:flex;flex-direction:column;gap:8px;pointer-events:none;max-width:min(360px,calc(100vw - 28px))}
.fr-toast{pointer-events:auto;padding:11px 14px;border-radius:12px;background:#111827;color:#fff;font-size:13.5px;font-weight:600;line-height:1.4;
  border:1px solid rgba(255,255,255,.12);box-shadow:0 10px 30px rgba(0,0,0,.4);animation:fr-toast-in .22s ease-out}
.fr-toast--error{border-color:rgba(239,68,68,.6)}
@keyframes fr-toast-in{from{transform:translateY(-8px);opacity:0}to{transform:none;opacity:1}}
@media (max-width:767px){.fr-toasts{left:14px;right:14px;max-width:none}}
@media (prefers-reduced-motion:reduce){.fr-card *,.fr-toast,.fr-confirm,.fr-modal .fr-card{animation:none!important;transition:none!important}}
`;
injectStyles('mf-friends-panel-css', FRIENDS_CSS);

/* ------------------------------------------------------------------ */
/* Small pieces                                                        */
/* ------------------------------------------------------------------ */
function Empty({ icon, title, text, children }) {
  return (
    <div className="fr-empty">
      <div className="fr-empty-ico" aria-hidden="true">{icon}</div>
      <h4>{title}</h4>
      {text && <p>{text}</p>}
      {children}
    </div>
  );
}

function Skeleton() {
  return (
    <>
      {[0, 1, 2].map((i) => <div className="fr-sk" key={i} aria-hidden="true" />)}
    </>
  );
}

function PersonRow({ person, sub, onOpen, children }) {
  const name = userName(person);
  return (
    <div className="fr-row">
      <button type="button" className="fr-row-main" onClick={onOpen} aria-label={`Open ${name}'s profile`}>
        <FriendAvatar user={person} size={44} />
        <span className="fr-row-text">
          <span className="fr-row-name">{name}</span>
          {sub && <span className="fr-row-sub">{sub}</span>}
        </span>
      </button>
      <div className="fr-row-actions">{children}</div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Panel                                                               */
/* ------------------------------------------------------------------ */
export default function FriendsPanel({
  mode = 'sidebar',
  onClose,
  collapsible = false,
  collapseDirection = 'vertical',
  collapsed: collapsedProp,
  onToggleCollapsed,
  className = '',
  onReceivedCount,
}) {
  const m = mode === 'full' ? 'full' : 'sidebar';
  const sideMode = collapseDirection === 'side';
  const navigate = useNavigate();
  const { user } = useAuth();
  const me = user?.id;

  const [users, setUsers] = useState([]);
  const [extraUsers, setExtraUsers] = useState({});
  const [relations, setRelations] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [loadingRel, setLoadingRel] = useState(true);
  const [tab, setTab] = useState('forYou'); // forYou | myRelation | received | requested | blocked
  const [query, setQuery] = useState('');
  const [limit, setLimit] = useState(PAGE_SIZE);
  const [dismissed, setDismissed] = useState([]);
  const [busy, setBusy] = useState(null);
  const [toasts, setToasts] = useState([]);
  const [confirmState, setConfirmState] = useState(null);
  const [innerCollapsed, setInnerCollapsed] = useState(true);

  const audioRef = useRef(null);
  const extraRef = useRef({});
  const userMapRef = useRef({});
  const dismissKey = `mf-friends-hidden-${me || 'anon'}`;

  const controlled = typeof collapsedProp === 'boolean';
  const collapsedNow = controlled ? collapsedProp : innerCollapsed;
  const isCollapsed = collapsible && collapsedNow;
  const toggleCollapsed = () => {
    if (controlled) onToggleCollapsed?.();
    else setInnerCollapsed((c) => !c);
  };

  /* ---------- utilities ---------- */
  const pushToast = useCallback((text, kind) => {
    const id = Math.random().toString(36).slice(2, 9);
    setToasts((t) => [...t, { id, text, kind }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 4500);
  }, []);

  const playNotif = useCallback(() => {
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      if (!audioRef.current) audioRef.current = new AC();
      const ctx = audioRef.current;
      if (ctx.state === 'suspended') ctx.resume().catch(() => {});
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = 'sine';
      o.frequency.value = 880;
      g.gain.setValueAtTime(0.08, ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.00001, ctx.currentTime + 0.25);
      o.connect(g);
      g.connect(ctx.destination);
      o.start();
      o.stop(ctx.currentTime + 0.27);
    } catch { /* sound is optional */ }
  }, []);

  useEffect(() => () => {
    try { audioRef.current?.close(); } catch { /* ignore */ }
  }, []);

  /* ---------- data: users ---------- */
  useEffect(() => {
    if (!me) { setUsers([]); setLoadingUsers(false); return undefined; }
    let alive = true;
    (async () => {
      setLoadingUsers(true);
      try {
        const res = await pb.collection('users').getList(1, 100, {
          sort: '-created',
          filter: `id != "${me}"`,
          $autoCancel: false,
        });
        if (alive) setUsers(res.items || []);
      } catch (err) {
        console.error('Failed to load users', err);
      } finally {
        if (alive) setLoadingUsers(false);
      }
    })();
    return () => { alive = false; };
  }, [me]);

  /* ---------- data: relations ---------- */
  const fetchRelations = useCallback(async () => {
    if (!me) return;
    try {
      const list = await pb.collection('relation_requests').getFullList({
        filter: `fromUser = "${me}" || toUser = "${me}"`,
        $autoCancel: false,
      });
      setRelations(list);
    } catch (err) {
      console.warn('Failed to load relation_requests', err);
    } finally {
      setLoadingRel(false);
    }
  }, [me]);

  useEffect(() => {
    if (!me) { setRelations([]); setLoadingRel(false); return; }
    setLoadingRel(true);
    fetchRelations();
  }, [me, fetchRelations]);

  // hidden suggestions are remembered per user
  useEffect(() => {
    try { setDismissed(JSON.parse(localStorage.getItem(dismissKey) || '[]')); } catch { setDismissed([]); }
  }, [dismissKey]);

  // people who appear in relations but not in the first 100 users
  useEffect(() => {
    extraRef.current = extraUsers;
  }, [extraUsers]);

  useEffect(() => {
    const known = new Set(users.map((u) => u.id));
    const missing = [...new Set(relations.flatMap((r) => [r.fromUser, r.toUser]))]
      .filter((id) => id && id !== me && !known.has(id) && !extraRef.current[id]);
    if (!missing.length) return undefined;
    let alive = true;
    pb.collection('users').getFullList({
      filter: missing.map((id) => `id="${id}"`).join(' || '),
      $autoCancel: false,
    }).then((list) => {
      if (!alive) return;
      setExtraUsers((prev) => {
        const next = { ...prev };
        missing.forEach((id) => { next[id] = { id, name: 'Unknown user' }; });
        list.forEach((u) => { next[u.id] = u; });
        return next;
      });
    }).catch(() => {});
    return () => { alive = false; };
  }, [relations, users, me]);

  const userMap = useMemo(() => {
    const map = { ...extraUsers };
    users.forEach((u) => { map[u.id] = u; });
    return map;
  }, [users, extraUsers]);

  useEffect(() => { userMapRef.current = userMap; }, [userMap]);
  const personOf = (id) => userMap[id] || { id, name: 'Member' };

  /* ---------- realtime + gentle polling ---------- */
  useEffect(() => {
    if (!me) return undefined;
    let cancelled = false;
    let unsub = null;
    let timer = null;
    const schedule = () => { clearTimeout(timer); timer = setTimeout(fetchRelations, 250); };

    const nameOf = async (id) => {
      const known = userMapRef.current[id];
      if (known) return userName(known);
      try { return userName(await pb.collection('users').getOne(id, { $autoCancel: false })); } catch { return 'Someone'; }
    };

    try {
      Promise.resolve(pb.collection('relation_requests').subscribe('*', async (e) => {
        const rec = e?.record;
        if (!rec || (rec.fromUser !== me && rec.toUser !== me)) return;
        schedule();
        try {
          if (e.action === 'create' && rec.toUser === me && rec.status === 'pending') {
            playNotif();
            pushToast(`${await nameOf(rec.fromUser)} sent you a ${relLabel(rec.relationType).toLowerCase()} request`);
          }
          if (e.action === 'update' && rec.fromUser === me && rec.status === 'accepted') {
            playNotif();
            pushToast(`${await nameOf(rec.toUser)} accepted your request. Say hi! ??`);
          }
          if (e.action === 'update' && rec.toUser === me && rec.status === 'pending') {
            playNotif();
            pushToast(`${await nameOf(rec.fromUser)} sent you a ${relLabel(rec.relationType).toLowerCase()} request`);
          }
        } catch { /* notifications are best effort */ }
      })).then((fn) => {
        if (cancelled) { try { fn?.(); } catch { /* ignore */ } } else unsub = fn;
      }).catch((err) => console.warn('Realtime for relation_requests unavailable', err));
    } catch (err) {
      console.warn('Realtime for relation_requests unavailable', err);
    }

    const poll = setInterval(() => {
      if (document.visibilityState === 'visible') fetchRelations();
    }, POLL_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
      clearInterval(poll);
      try { unsub?.(); } catch { /* ignore */ }
    };
  }, [me, fetchRelations, playNotif, pushToast]);

  /* ---------- derived lists ---------- */
  const rel = useMemo(() => {
    const out = { accepted: [], received: [], requested: [], blocked: [], excluded: new Set([me]) };
    for (const r of relations) {
      const mine = r.fromUser === me;
      const other = mine ? r.toUser : r.fromUser;
      if (r.status === 'blocked' || r.relationType === 'block') {
        out.excluded.add(other);
        if (mine) out.blocked.push(r);
      } else if (r.status === 'accepted') {
        out.accepted.push(r);
        out.excluded.add(other);
      } else if (r.status === 'pending') {
        out.excluded.add(other);
        (mine ? out.requested : out.received).push(r);
      } else if (mine && (r.status === 'terminated' || r.status === 'rejected')) {
        out.requested.push(r);
        if (inCooldown(r)) out.excluded.add(other);
      }
    }
    return out;
  }, [relations, me]);

  useEffect(() => { onReceivedCount?.(rel.received.length); }, [rel.received.length, onReceivedCount]);

  const q = query.trim().toLowerCase();
  const matches = (u) => !q || [u?.name, u?.username, u?.city, u?.bio, u?.shortInfo]
    .some((v) => v && String(v).toLowerCase().includes(q));

  const forYouAll = useMemo(
    () => users.filter((u) => !rel.excluded.has(u.id) && !dismissed.includes(u.id)),
    [users, rel, dismissed],
  );
  const forYou = forYouAll.filter(matches);

  const byNewest = (a, b) => String(b.createdAt || b.created || '').localeCompare(String(a.createdAt || a.created || ''));
  const otherId = (r) => (r.fromUser === me ? r.toUser : r.fromUser);

  useEffect(() => { setLimit(PAGE_SIZE); }, [tab, query]);

  /* ---------- actions ---------- */
  const askConfirm = (cfg) => setConfirmState(cfg);

  const run = async (key, fn) => {
    setBusy(key);
    try {
      await fn();
    } catch (err) {
      console.error(err);
      pushToast('Something went wrong. Please try again.', 'error');
    } finally {
      setBusy(null);
    }
  };

  const upsertLocal = (rec) => setRelations((prev) => [...prev.filter((x) => x.id !== rec.id), rec]);

  const sendRequest = async (toId, relationType) => {
    if (!me) { pushToast('Please log in first.', 'error'); throw new Error('unauthenticated'); }
    try {
      const existing = relations.find((r) => r.fromUser === me && r.toUser === toId && r.relationType !== 'block');
      const now = new Date().toISOString();
      let rec;
      if (existing && (existing.status === 'terminated' || existing.status === 'rejected')) {
        if (inCooldown(existing)) {
          pushToast(`You can send this again in ${waitLabel(existing.resendAvailableAt)}.`, 'error');
          throw new Error('cooldown');
        }
        rec = await pb.collection('relation_requests').update(existing.id, {
          relationType, status: 'pending', createdAt: now, terminatedAt: null, rejectedAt: null, resendAvailableAt: null,
        });
      } else {
        rec = await pb.collection('relation_requests').create({
          fromUser: me, toUser: toId, relationType, status: 'pending', createdAt: now,
        });
      }
      upsertLocal(rec);
      pushToast('Request sent ?');
      return rec;
    } catch (err) {
      if (err?.message !== 'cooldown') pushToast('Could not send the request. Please try again.', 'error');
      throw err;
    }
  };

  const resendRequest = (r) => run(r.id, async () => {
    const rec = await pb.collection('relation_requests').update(r.id, {
      status: 'pending', createdAt: new Date().toISOString(), terminatedAt: null, rejectedAt: null, resendAvailableAt: null,
    });
    upsertLocal(rec);
    pushToast('Request sent again ?');
  });

  const acceptRequest = (r) => run(r.id, async () => {
    const rec = await pb.collection('relation_requests').update(r.id, { status: 'accepted', acceptedAt: new Date().toISOString() });
    upsertLocal(rec);
    pushToast(`You're now connected with ${userName(personOf(r.fromUser))} ??`);
  });

  const rejectRequest = (r) => askConfirm({
    title: 'Decline this request?',
    text: `${userName(personOf(r.fromUser))} will not be able to send another for 24 hours.`,
    confirmLabel: 'Decline',
    danger: true,
    onConfirm: () => run(r.id, async () => {
      const now = new Date();
      const rec = await pb.collection('relation_requests').update(r.id, {
        status: 'rejected',
        rejectedAt: now.toISOString(),
        resendAvailableAt: new Date(now.getTime() + COOLDOWN_MS).toISOString(),
      });
      upsertLocal(rec);
      pushToast('Request declined');
    }),
  });

  const terminateRequest = (r) => askConfirm({
    title: 'Cancel this request?',
    text: 'If you cancel it, you can send a new one only after 24 hours.',
    confirmLabel: 'Cancel request',
    danger: true,
    onConfirm: () => run(r.id, async () => {
      const now = new Date();
      const rec = await pb.collection('relation_requests').update(r.id, {
        status: 'terminated',
        terminatedAt: now.toISOString(),
        resendAvailableAt: new Date(now.getTime() + COOLDOWN_MS).toISOString(),
      });
      upsertLocal(rec);
      pushToast('Request cancelled. You can resend after 24 hours.');
    }),
  });

  const removeRelation = (r) => askConfirm({
    title: 'Remove this relation?',
    text: `You and ${userName(personOf(otherId(r)))} will no longer be connected.`,
    confirmLabel: 'Remove',
    danger: true,
    onConfirm: () => run(r.id, async () => {
      await pb.collection('relation_requests').delete(r.id);
      setRelations((prev) => prev.filter((x) => x.id !== r.id));
      pushToast('Relation removed');
    }),
  });

  const blockUser = (targetId) => askConfirm({
    title: `Block ${userName(personOf(targetId))}?`,
    text: 'They will disappear from your suggestions and any connection or request between you is removed.',
    confirmLabel: 'Block',
    danger: true,
    onConfirm: () => run(`b-${targetId}`, async () => {
      const pair = relations.filter((r) => (r.fromUser === me && r.toUser === targetId) || (r.fromUser === targetId && r.toUser === me));
      const mine = pair.find((r) => r.fromUser === me);
      const now = new Date().toISOString();
      const blockRec = mine
        ? await pb.collection('relation_requests').update(mine.id, { relationType: 'block', status: 'blocked', blockedAt: now })
        : await pb.collection('relation_requests').create({ fromUser: me, toUser: targetId, relationType: 'block', status: 'blocked', createdAt: now });
      for (const r of pair) {
        if (r.id === blockRec.id) continue;
        if (r.status === 'accepted' || r.status === 'pending') {
          try { await pb.collection('relation_requests').delete(r.id); } catch { /* may be restricted by rules */ }
        }
      }
      await fetchRelations();
      pushToast('User blocked');
    }),
  });

  const unblock = (r) => run(r.id, async () => {
    await pb.collection('relation_requests').delete(r.id);
    setRelations((prev) => prev.filter((x) => x.id !== r.id));
    pushToast('User unblocked');
  });

  const dismissSuggestion = (id) => {
    const next = [...new Set([...dismissed, id])].slice(-300);
    setDismissed(next);
    try { localStorage.setItem(dismissKey, JSON.stringify(next)); } catch { /* ignore */ }
  };

  const restoreSuggestions = () => {
    setDismissed([]);
    try { localStorage.removeItem(dismissKey); } catch { /* ignore */ }
  };

  const openCall = (person) => window.dispatchEvent(new CustomEvent('openCallUI', { detail: { peerId: person.peerId } }));
  const goProfile = (id) => navigate(`/profile/${id}`);

  /* ---------- modal behaviour ---------- */
  useEffect(() => {
    if (m !== 'full') return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [m, onClose]);

  /* ---------- tab bodies ---------- */
  const noMatch = (
    <Empty icon="??" title="No matches" text="Try a different name, city or keyword." />
  );

  const renderBody = () => {
    if (!me) {
      return (
        <Empty icon="??" title="Log in to find people" text="Sign in to see suggestions, requests and your connections.">
          <Link to="/login" className="fr-btn fr-btn--primary">Log in</Link>
        </Empty>
      );
    }

    if (tab === 'forYou') {
      if (loadingUsers || loadingRel) return <Skeleton />;
      if (forYouAll.length === 0) {
        return (
          <Empty icon="?" title="You're all caught up" text="No new suggestions right now. Check back soon.">
            {dismissed.length > 0 && (
              <button type="button" className="fr-btn" onClick={restoreSuggestions}>Show hidden suggestions</button>
            )}
          </Empty>
        );
      }
      if (forYou.length === 0) return noMatch;
      return (
        <>
          {forYou.slice(0, limit).map((u) => (
            <FriendTile
              key={u.id}
              userRecord={u}
              currentUserId={me}
              onSendRequest={sendRequest}
              onBlock={blockUser}
              onRemove={dismissSuggestion}
              compact={m === 'sidebar'}
            />
          ))}
          {forYou.length > limit && (
            <button type="button" className="fr-btn fr-more" onClick={() => setLimit((l) => l + PAGE_SIZE)}>
              Show more ({forYou.length - limit})
            </button>
          )}
        </>
      );
    }

    if (loadingRel) return <Skeleton />;

    if (tab === 'myRelation') {
      const items = [...rel.accepted].sort(byNewest).filter((r) => matches(personOf(otherId(r))));
      if (rel.accepted.length === 0) {
        return <Empty icon="??" title="No relations yet" text="Send a few requests from �For you� and your connections will show up here." />;
      }
      if (items.length === 0) return noMatch;
      return items.map((r) => {
        const p = personOf(otherId(r));
        return (
          <PersonRow key={r.id} person={p} onOpen={() => goProfile(p.id)} sub={<span className="fr-pill">{relLabel(r.relationType)}</span>}>
            <button type="button" className="fr-btn fr-btn--primary" onClick={() => navigate(`/chat/${p.id}`)}>
              <ChatBubbleRoundedIcon /> Chat
            </button>
            <button
              type="button"
              className="fr-btn fr-btn--icon"
              onClick={() => openCall(p)}
              disabled={!p.peerId}
              aria-label={`Call ${userName(p)}`}
              title={p.peerId ? 'Call' : 'Calling unavailable'}
            >
              <CallRoundedIcon />
            </button>
            <button
              type="button"
              className="fr-btn fr-btn--icon fr-btn--danger"
              onClick={() => removeRelation(r)}
              disabled={busy === r.id}
              aria-label={`Remove ${userName(p)}`}
              title="Remove relation"
            >
              <PersonRemoveRoundedIcon />
            </button>
          </PersonRow>
        );
      });
    }

    if (tab === 'received') {
      const items = [...rel.received].sort(byNewest).filter((r) => matches(personOf(r.fromUser)));
      if (rel.received.length === 0) {
        return <Empty icon="??" title="No incoming requests" text="When someone wants to connect with you, it will show up here." />;
      }
      if (items.length === 0) return noMatch;
      return items.map((r) => {
        const p = personOf(r.fromUser);
        return (
          <PersonRow key={r.id} person={p} onOpen={() => goProfile(p.id)} sub={<>wants to be your <span className="fr-pill">{relLabel(r.relationType)}</span></>}>
            <button type="button" className="fr-btn fr-btn--ok" onClick={() => acceptRequest(r)} disabled={busy === r.id}>
              <CheckRoundedIcon /> Accept
            </button>
            <button type="button" className="fr-btn" onClick={() => rejectRequest(r)} disabled={busy === r.id}>Decline</button>
            <button
              type="button"
              className="fr-btn fr-btn--icon fr-btn--danger"
              onClick={() => blockUser(p.id)}
              aria-label={`Block ${userName(p)}`}
              title="Block"
            >
              <BlockRoundedIcon />
            </button>
          </PersonRow>
        );
      });
    }

    if (tab === 'requested') {
      const items = [...rel.requested].sort(byNewest).filter((r) => matches(personOf(r.toUser)));
      if (rel.requested.length === 0) {
        return <Empty icon="??" title="No requests sent" text="Requests you send will be tracked here." />;
      }
      if (items.length === 0) return noMatch;
      return items.map((r) => {
        const p = personOf(r.toUser);
        const cooling = inCooldown(r);
        const statusPill = r.status === 'pending'
          ? <span className="fr-pill fr-pill--warn">Pending</span>
          : <span className="fr-pill fr-pill--muted">{r.status === 'rejected' ? 'Declined' : 'Cancelled'}</span>;
        return (
          <PersonRow key={r.id} person={p} onOpen={() => goProfile(p.id)} sub={<>{statusPill}<span>{relLabel(r.relationType)}</span></>}>
            {r.status === 'pending' && (
              <button type="button" className="fr-btn fr-btn--danger" onClick={() => terminateRequest(r)} disabled={busy === r.id}>Cancel</button>
            )}
            {r.status !== 'pending' && (
              <button type="button" className="fr-btn fr-btn--primary" onClick={() => resendRequest(r)} disabled={cooling || busy === r.id}>
                <ReplayRoundedIcon /> {cooling ? `in ${waitLabel(r.resendAvailableAt)}` : 'Resend'}
              </button>
            )}
          </PersonRow>
        );
      });
    }

    // blocked
    const items = [...rel.blocked].sort(byNewest).filter((r) => matches(personOf(r.toUser)));
    if (rel.blocked.length === 0) {
      return <Empty icon="???" title="No blocked users" text="People you block will be listed here, and you can unblock them any time." />;
    }
    if (items.length === 0) return noMatch;
    return items.map((r) => {
      const p = personOf(r.toUser);
      return (
        <PersonRow key={r.id} person={p} onOpen={() => goProfile(p.id)} sub="Blocked by you">
          <button type="button" className="fr-btn" onClick={() => unblock(r)} disabled={busy === r.id}>Unblock</button>
        </PersonRow>
      );
    });
  };

  const tabs = [
    { id: 'forYou', label: 'For you', n: forYouAll.length },
    { id: 'myRelation', label: 'Relations', n: rel.accepted.length },
    { id: 'received', label: 'Received', n: rel.received.length, alert: true },
    { id: 'requested', label: 'Requested', n: rel.requested.length },
    { id: 'blocked', label: 'Blocked', n: rel.blocked.length },
  ];

  const showClose = !!onClose;
  const verticalCollapsed = isCollapsed && !sideMode;

  const card = (
    <section
      className={`fr fr-card${verticalCollapsed ? ' is-collapsed' : ''}${className ? ` ${className}` : ''}`}
      aria-label="Friends"
    >
      <div className="fr-head">
        <h2 className="fr-title">
          <span className="fr-title-ico" aria-hidden="true"><PeopleAltRoundedIcon /></span>
          Friends
          {verticalCollapsed && rel.received.length > 0 && <span className="fr-new">{rel.received.length} new</span>}
        </h2>
        {collapsible && sideMode && (
          <button
            type="button"
            className="fr-iconbtn"
            aria-expanded="true"
            aria-label="Collapse friends panel"
            title="Collapse"
            onClick={toggleCollapsed}
          >
            <ChevronLeftRoundedIcon />
          </button>
        )}
        {collapsible && !sideMode && (
          <button
            type="button"
            className="fr-iconbtn fr-chev"
            aria-expanded={!isCollapsed}
            aria-label={isCollapsed ? 'Expand friends panel' : 'Collapse friends panel'}
            onClick={toggleCollapsed}
          >
            <ExpandMoreRoundedIcon />
          </button>
        )}
        {showClose && (
          <button type="button" className="fr-iconbtn" aria-label="Close" onClick={onClose}>
            <CloseRoundedIcon />
          </button>
        )}
      </div>

      <div className="fr-tabs fr-fold" role="tablist" aria-label="Friends sections">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            className={`fr-tab${tab === t.id ? ' is-active' : ''}`}
            onClick={() => setTab(t.id)}
          >
            {t.label}
            {t.n > 0 && <span className={`fr-tab-n${t.alert ? ' is-alert' : ''}`}>{t.n}</span>}
          </button>
        ))}
      </div>

      {me && (
        <div className="fr-search fr-fold">
          <SearchRoundedIcon />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search people"
            aria-label="Search people"
          />
        </div>
      )}

      <div className="fr-body fr-fold" role="tabpanel">
        {renderBody()}
      </div>

      {confirmState && (
        <div className="fr-confirm" role="presentation" onMouseDown={(e) => { if (e.target === e.currentTarget) setConfirmState(null); }}>
          <div className="fr-confirm-box" role="alertdialog" aria-modal="true" aria-label={confirmState.title}>
            <h4>{confirmState.title}</h4>
            <p>{confirmState.text}</p>
            <div className="fr-confirm-btns">
              <button type="button" className="fr-btn" autoFocus onClick={() => setConfirmState(null)}>Keep</button>
              <button
                type="button"
                className={`fr-btn ${confirmState.danger ? 'fr-btn--danger' : 'fr-btn--primary'}`}
                onClick={() => { const fn = confirmState.onConfirm; setConfirmState(null); fn?.(); }}
              >
                {confirmState.confirmLabel || 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );

  // desktop column collapsed: slim rail with a right arrow to broaden it again
  const rail = (
    <section className="fr fr-rail" aria-label="Friends (collapsed)">
      <button
        type="button"
        className="fr-iconbtn"
        aria-expanded="false"
        aria-label="Expand friends panel"
        title="Expand"
        onClick={toggleCollapsed}
      >
        <ChevronRightRoundedIcon />
      </button>
      <span className="fr-rail-ico" aria-hidden="true">
        <PeopleAltRoundedIcon />
        {rel.received.length > 0 && <span className="fr-rail-badge">{rel.received.length > 9 ? '9+' : rel.received.length}</span>}
      </span>
      <span className="fr-rail-label">Friends</span>
    </section>
  );

  const toastLayer = typeof document !== 'undefined' && createPortal(
    <div className="fr-toasts" role="status" aria-live="polite">
      {toasts.map((t) => (
        <div key={t.id} className={`fr-toast${t.kind === 'error' ? ' fr-toast--error' : ''}`}>{t.text}</div>
      ))}
    </div>,
    document.body,
  );

  if (m === 'full') {
    return (
      <>
        <div
          className="fr fr-modal"
          role="presentation"
          onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
        >
          {card}
        </div>
        {toastLayer}
      </>
    );
  }

  return (
    <>
      {isCollapsed && sideMode ? rail : card}
      {toastLayer}
    </>
  );
}